/**
 * DatePicker (UI/UX follow-up F-06): a Nigerian DD/MM/YYYY date field with a calendar
 * Popover — no date library. The value is an ISO `YYYY-MM-DD` string (or '' when empty)
 * and every piece of day maths runs on `{ y, m, d }` parts through `Date.UTC` getters:
 * `new Date('YYYY-MM-DD')` parses as UTC midnight, which shifts the calendar day for any
 * browser west of Greenwich, so it is never used here. `Intl` is used only for the
 * month/weekday names (with a fixed fallback) and for "today" in Africa/Lagos
 * (design-system.md §9 — WAT is the product zone).
 *
 * Composition: `FieldFrame` owns the label / help / error chrome exactly as `Input` does,
 * so the input's accessible name is precisely the label; `Popover` owns the calendar panel
 * (Escape, outside click, focus restore). The text input accepts `D/M/YYYY`, `DD-MM-YYYY`
 * and `DD.MM.YYYY` too; it emits as soon as a full valid date is typed and normalises the
 * text on blur/Enter, or shows "Enter a date as DD/MM/YYYY" inline.
 */
import { Calendar, Linear } from './icons.ts';
import {
  useCallback,
  useEffect,
  useId,
  useMemo,
  useRef,
  useState,
  type ChangeEvent,
  type KeyboardEvent,
  type ReactNode,
} from 'react';

import { Button } from './button.tsx';
import { FieldFrame, type FieldChromeProps } from './field.tsx';
import { Popover } from './popover.tsx';

const { ChevronLeft, ChevronRight } = Linear;

/* ── Timezone-safe date parts ─────────────────────────────────────────── */

interface DateParts {
  readonly y: number;
  readonly m: number; // 1–12
  readonly d: number; // 1–31
}

const ISO_RE = /^(\d{4})-(\d{2})-(\d{2})$/;
const DISPLAY_RE = /^\s*(\d{1,2})[/.-](\d{1,2})[/.-](\d{4})\s*$/;

function isLeapYear(y: number): boolean {
  return (y % 4 === 0 && y % 100 !== 0) || y % 400 === 0;
}

function daysInMonth(y: number, m: number): number {
  const days = [31, isLeapYear(y) ? 29 : 28, 31, 30, 31, 30, 31, 31, 30, 31, 30, 31];
  return days[m - 1] ?? 31;
}

function isValidParts({ y, m, d }: DateParts): boolean {
  return (
    Number.isInteger(y) &&
    Number.isInteger(m) &&
    Number.isInteger(d) &&
    y >= 1000 &&
    y <= 9999 &&
    m >= 1 &&
    m <= 12 &&
    d >= 1 &&
    d <= daysInMonth(y, m)
  );
}

function parseIsoDate(iso: string): DateParts | null {
  const match = ISO_RE.exec(iso);
  if (!match) return null;
  const parts = { y: Number(match[1]), m: Number(match[2]), d: Number(match[3]) };
  return isValidParts(parts) ? parts : null;
}

function parseDisplayDate(text: string): DateParts | null {
  const match = DISPLAY_RE.exec(text);
  if (!match) return null;
  const parts = { y: Number(match[3]), m: Number(match[2]), d: Number(match[1]) };
  return isValidParts(parts) ? parts : null;
}

function pad2(n: number): string {
  return String(n).padStart(2, '0');
}

function toIsoDate({ y, m, d }: DateParts): string {
  return `${String(y).padStart(4, '0')}-${pad2(m)}-${pad2(d)}`;
}

function formatDisplayDate({ y, m, d }: DateParts): string {
  return `${pad2(d)}/${pad2(m)}/${String(y).padStart(4, '0')}`;
}

/** `setUTCFullYear` (not `Date.UTC`) so years 0–99 are not remapped to 19xx. */
function toUtc({ y, m, d }: DateParts, dayOffset = 0): Date {
  const dt = new Date(0);
  dt.setUTCFullYear(y, m - 1, d + dayOffset);
  return dt;
}

function fromUtc(dt: Date): DateParts {
  return { y: dt.getUTCFullYear(), m: dt.getUTCMonth() + 1, d: dt.getUTCDate() };
}

function addDays(p: DateParts, n: number): DateParts {
  return fromUtc(toUtc(p, n));
}

/** Monday = 0 … Sunday = 6 (weeks start on Monday). */
function weekday(p: DateParts): number {
  return (toUtc(p).getUTCDay() + 6) % 7;
}

function addMonths(p: DateParts, n: number): DateParts {
  const total = p.y * 12 + (p.m - 1) + n;
  const y = Math.floor(total / 12);
  const m = total - y * 12 + 1;
  return { y, m, d: Math.min(p.d, daysInMonth(y, m)) };
}

function compareParts(a: DateParts, b: DateParts): number {
  return a.y - b.y || a.m - b.m || a.d - b.d;
}

function monthIndex(p: DateParts): number {
  return p.y * 12 + (p.m - 1);
}

/** Today's calendar date in Africa/Lagos, falling back to the browser's local date. */
function todayParts(): DateParts {
  try {
    const parts = new Intl.DateTimeFormat('en-GB', {
      timeZone: 'Africa/Lagos',
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
    }).formatToParts(new Date());
    const pick = (type: Intl.DateTimeFormatPartTypes): number =>
      Number(parts.find((part) => part.type === type)?.value);
    const lagos = { y: pick('year'), m: pick('month'), d: pick('day') };
    if (isValidParts(lagos)) return lagos;
  } catch {
    // Intl timeZone unsupported — use the local date below.
  }
  const now = new Date();
  return { y: now.getFullYear(), m: now.getMonth() + 1, d: now.getDate() };
}

/* ── Names (Intl 'en-NG' with a fixed fallback) ───────────────────────── */

const MONTH_FALLBACK: readonly string[] = [
  'January',
  'February',
  'March',
  'April',
  'May',
  'June',
  'July',
  'August',
  'September',
  'October',
  'November',
  'December',
];
const WEEKDAY_FALLBACK: readonly string[] = [
  'Monday',
  'Tuesday',
  'Wednesday',
  'Thursday',
  'Friday',
  'Saturday',
  'Sunday',
];
const WEEKDAY_SHORT: readonly string[] = ['Mo', 'Tu', 'We', 'Th', 'Fr', 'Sa', 'Su'];

function intlNames(
  fallback: readonly string[],
  options: Intl.DateTimeFormatOptions,
  dateAt: (i: number) => Date,
): readonly string[] {
  try {
    const fmt = new Intl.DateTimeFormat('en-NG', { ...options, timeZone: 'UTC' });
    const names = fallback.map((_, i) => fmt.format(dateAt(i)));
    return names.every((name) => name.length > 0) ? names : fallback;
  } catch {
    return fallback;
  }
}

const MONTH_NAMES = intlNames(MONTH_FALLBACK, { month: 'long' }, (i) =>
  toUtc({ y: 2000, m: i + 1, d: 15 }),
);
// 2024-01-01 is a Monday.
const WEEKDAY_NAMES = intlNames(WEEKDAY_FALLBACK, { weekday: 'long' }, (i) =>
  toUtc({ y: 2024, m: 1, d: 1 + i }),
);

function monthName(m: number): string {
  return MONTH_NAMES[m - 1] ?? MONTH_FALLBACK[m - 1] ?? '';
}

/* ── Component ────────────────────────────────────────────────────────── */

export const DATE_FORMAT_ERROR = 'Enter a date as DD/MM/YYYY';

function rangeError(min: DateParts | null, max: DateParts | null): string {
  if (min && max) {
    return `Enter a date between ${formatDisplayDate(min)} and ${formatDisplayDate(max)}`;
  }
  if (min) return `Enter a date on or after ${formatDisplayDate(min)}`;
  if (max) return `Enter a date on or before ${formatDisplayDate(max)}`;
  return DATE_FORMAT_ERROR;
}

function describedBy(id: string, help: ReactNode, error: ReactNode): string | undefined {
  const ids = [error ? `${id}-error` : '', help ? `${id}-help` : ''].filter(Boolean);
  return ids.length > 0 ? ids.join(' ') : undefined;
}

export interface DatePickerProps extends FieldChromeProps {
  /** ISO `YYYY-MM-DD`, or '' when empty. An unparsable value renders as empty. */
  value: string;
  /** Fires with an ISO `YYYY-MM-DD` string, or '' when the field is cleared. */
  onChange: (iso: string) => void;
  /** Earliest selectable date (ISO). Earlier days are disabled; typed ones are rejected. */
  min?: string | undefined;
  /** Latest selectable date (ISO). */
  max?: string | undefined;
  disabled?: boolean | undefined;
  id?: string | undefined;
  /** Rendered on a hidden input carrying the ISO value, so a plain form submit gets ISO. */
  name?: string | undefined;
  placeholder?: string | undefined;
  fieldSize?: 'sm' | 'md' | undefined;
  className?: string | undefined;
}

export function DatePicker({
  label,
  required,
  optional,
  help,
  error,
  hideLabel,
  value,
  onChange,
  min,
  max,
  disabled,
  id,
  name,
  placeholder = 'DD/MM/YYYY',
  fieldSize,
  className,
}: DatePickerProps): JSX.Element {
  const autoId = useId();
  const inputId = id ?? autoId;
  const monthLabelId = `${inputId}-month`;
  const inputRef = useRef<HTMLInputElement>(null);
  const gridRef = useRef<HTMLDivElement>(null);

  const valueParts = useMemo(() => parseIsoDate(value), [value]);
  const minParts = useMemo(() => (min ? parseIsoDate(min) : null), [min]);
  const maxParts = useMemo(() => (max ? parseIsoDate(max) : null), [max]);

  const [text, setText] = useState(() => (valueParts ? formatDisplayDate(valueParts) : ''));
  const [localError, setLocalError] = useState<string | null>(null);
  const [open, setOpen] = useState(false);
  const [today, setToday] = useState(todayParts);
  const [focusDay, setFocusDay] = useState<DateParts>(() => valueParts ?? today);
  // Set by keyboard navigation / opening so the effect below moves DOM focus only then
  // (clicking "Next month" must not steal focus from that button).
  const focusRequest = useRef(false);
  // The last ISO we emitted: an external `value` change that is not our own echo resets
  // the text, while an echo leaves in-progress typing untouched.
  const lastEmitted = useRef(value);

  useEffect(() => {
    if (value === lastEmitted.current) return;
    lastEmitted.current = value;
    const parts = parseIsoDate(value);
    setText(parts ? formatDisplayDate(parts) : '');
    setLocalError(null);
  }, [value]);

  const inRange = useCallback(
    (p: DateParts): boolean =>
      (!minParts || compareParts(p, minParts) >= 0) &&
      (!maxParts || compareParts(p, maxParts) <= 0),
    [minParts, maxParts],
  );
  const clampToRange = useCallback(
    (p: DateParts): DateParts => {
      if (minParts && compareParts(p, minParts) < 0) return minParts;
      if (maxParts && compareParts(p, maxParts) > 0) return maxParts;
      return p;
    },
    [minParts, maxParts],
  );

  const emit = useCallback(
    (iso: string): void => {
      if (iso === value) return;
      lastEmitted.current = iso;
      onChange(iso);
    },
    [onChange, value],
  );

  function commit(raw: string): void {
    if (raw.trim() === '') {
      setLocalError(null);
      setText('');
      emit('');
      return;
    }
    const parts = parseDisplayDate(raw);
    if (!parts) {
      setLocalError(DATE_FORMAT_ERROR);
      return;
    }
    if (!inRange(parts)) {
      setLocalError(rangeError(minParts, maxParts));
      return;
    }
    setLocalError(null);
    setText(formatDisplayDate(parts));
    emit(toIsoDate(parts));
  }

  function onInputChange(event: ChangeEvent<HTMLInputElement>): void {
    const next = event.target.value;
    setText(next);
    setLocalError(null);
    if (next.trim() === '') {
      emit('');
      return;
    }
    const parts = parseDisplayDate(next);
    if (parts && inRange(parts)) emit(toIsoDate(parts));
  }

  const handleOpenChange = useCallback(
    (next: boolean): void => {
      setOpen(next);
      if (!next) return;
      const now = todayParts();
      setToday(now);
      focusRequest.current = true;
      setFocusDay(
        clampToRange(valueParts ?? parseDisplayDate(inputRef.current?.value ?? '') ?? now),
      );
    },
    [clampToRange, valueParts],
  );

  function onInputKeyDown(event: KeyboardEvent<HTMLInputElement>): void {
    if (event.key === 'Enter') {
      commit(event.currentTarget.value);
    } else if (event.key === 'ArrowDown' && !open && !disabled) {
      event.preventDefault();
      handleOpenChange(true);
    }
  }

  function selectDay(p: DateParts): void {
    setLocalError(null);
    setText(formatDisplayDate(p));
    emit(toIsoDate(p));
    inputRef.current?.focus();
    setOpen(false);
  }

  function moveFocus(p: DateParts): void {
    focusRequest.current = true;
    setFocusDay(clampToRange(p));
  }

  function onGridKeyDown(event: KeyboardEvent<HTMLDivElement>): void {
    let next: DateParts | null = null;
    switch (event.key) {
      case 'ArrowLeft':
        next = addDays(focusDay, -1);
        break;
      case 'ArrowRight':
        next = addDays(focusDay, 1);
        break;
      case 'ArrowUp':
        next = addDays(focusDay, -7);
        break;
      case 'ArrowDown':
        next = addDays(focusDay, 7);
        break;
      case 'PageUp':
        next = addMonths(focusDay, event.shiftKey ? -12 : -1);
        break;
      case 'PageDown':
        next = addMonths(focusDay, event.shiftKey ? 12 : 1);
        break;
      case 'Home':
        next = addDays(focusDay, -weekday(focusDay));
        break;
      case 'End':
        next = addDays(focusDay, 6 - weekday(focusDay));
        break;
      default:
        return;
    }
    event.preventDefault();
    moveFocus(next);
  }

  // Move DOM focus to the roving day after opening or a keyboard move. This runs after
  // Popover's own open effect (a child), so it wins over Popover's "first focusable".
  useEffect(() => {
    if (!open || !focusRequest.current) return;
    focusRequest.current = false;
    gridRef.current
      ?.querySelector<HTMLButtonElement>(`[data-iso="${toIsoDate(focusDay)}"]`)
      ?.focus();
  }, [open, focusDay]);

  // Month grid: leading days from the previous month so the first row starts on Monday.
  const firstOfMonth: DateParts = { y: focusDay.y, m: focusDay.m, d: 1 };
  const lead = weekday(firstOfMonth);
  const weeks = Math.ceil((lead + daysInMonth(focusDay.y, focusDay.m)) / 7);
  const gridStart = addDays(firstOfMonth, -lead);
  const rows = Array.from({ length: weeks }, (_, w) =>
    Array.from({ length: 7 }, (_, i) => addDays(gridStart, w * 7 + i)),
  );
  const canPrev = !minParts || monthIndex(focusDay) > monthIndex(minParts);
  const canNext = !maxParts || monthIndex(focusDay) < monthIndex(maxParts);

  const mergedError = localError ?? error;
  const small = fieldSize === 'sm';

  return (
    <FieldFrame
      id={inputId}
      label={label}
      required={required}
      optional={optional}
      help={help}
      error={mergedError}
      hideLabel={hideLabel}
      className={className}
    >
      <div className="argus-datepicker">
        <input
          ref={inputRef}
          id={inputId}
          type="text"
          inputMode="numeric"
          autoComplete="off"
          className={['argus-input', 'argus-datepicker__input', small ? 'argus-input--sm' : '']
            .filter(Boolean)
            .join(' ')}
          value={text}
          placeholder={placeholder}
          disabled={disabled}
          required={required}
          aria-required={required || undefined}
          aria-invalid={mergedError ? true : undefined}
          aria-describedby={describedBy(inputId, help, mergedError)}
          onChange={onInputChange}
          onBlur={(event) => commit(event.currentTarget.value)}
          onKeyDown={onInputKeyDown}
        />
        {name ? <input type="hidden" name={name} value={value} /> : null}
        {/* Default `align` ("start") → `bottom-start`: the calendar's left edge lines up
            with the trigger button's. `Popover`'s `useFloating` clamps it inside the
            viewport itself, so the old `align="end"` right-alignment (a manual workaround
            for the button sitting at the field's right edge) is no longer needed. */}
        <Popover
          open={open}
          onOpenChange={handleOpenChange}
          aria-label="Calendar"
          className="argus-datepicker__calendar"
          trigger={
            <Button
              variant="ghost"
              size={small ? 'sm' : 'md'}
              iconOnly
              className="argus-datepicker__button"
              aria-label="Choose date"
              disabled={disabled}
              icon={<Calendar size={small ? 14 : 16} aria-hidden="true" />}
            />
          }
        >
          <div className="argus-datepicker__header">
            <Button
              variant="ghost"
              size="sm"
              iconOnly
              aria-label="Previous month"
              disabled={!canPrev}
              icon={<ChevronLeft size={16} aria-hidden="true" />}
              onClick={() => setFocusDay(clampToRange(addMonths(focusDay, -1)))}
            />
            <div id={monthLabelId} className="argus-datepicker__month" aria-live="polite">
              {monthName(focusDay.m)} {focusDay.y}
            </div>
            <Button
              variant="ghost"
              size="sm"
              iconOnly
              aria-label="Next month"
              disabled={!canNext}
              icon={<ChevronRight size={16} aria-hidden="true" />}
              onClick={() => setFocusDay(clampToRange(addMonths(focusDay, 1)))}
            />
          </div>
          <div
            ref={gridRef}
            role="grid"
            aria-labelledby={monthLabelId}
            className="argus-datepicker__grid"
            onKeyDown={onGridKeyDown}
          >
            <div role="row" className="argus-datepicker__row">
              {WEEKDAY_SHORT.map((short, i) => (
                <div key={short} role="columnheader" className="argus-datepicker__weekday">
                  <span aria-hidden="true">{short}</span>
                  <span className="sr-only">{WEEKDAY_NAMES[i] ?? WEEKDAY_FALLBACK[i]}</span>
                </div>
              ))}
            </div>
            {rows.map((row) => (
              <div
                key={toIsoDate(row[0] ?? firstOfMonth)}
                role="row"
                className="argus-datepicker__row"
              >
                {row.map((p) => {
                  const iso = toIsoDate(p);
                  const outside = p.m !== focusDay.m || p.y !== focusDay.y;
                  const isToday = compareParts(p, today) === 0;
                  const isSelected = valueParts !== null && compareParts(p, valueParts) === 0;
                  const isFocus = compareParts(p, focusDay) === 0;
                  return (
                    <button
                      key={iso}
                      type="button"
                      role="gridcell"
                      data-iso={iso}
                      tabIndex={isFocus ? 0 : -1}
                      disabled={!inRange(p)}
                      aria-label={`${p.d} ${monthName(p.m)} ${p.y}`}
                      aria-selected={isSelected || undefined}
                      aria-current={isToday ? 'date' : undefined}
                      className={[
                        'argus-datepicker__day',
                        isToday ? 'is-today' : '',
                        isSelected ? 'is-selected' : '',
                        outside ? 'is-outside' : '',
                      ]
                        .filter(Boolean)
                        .join(' ')}
                      onClick={() => selectDay(p)}
                    >
                      {p.d}
                    </button>
                  );
                })}
              </div>
            ))}
          </div>
        </Popover>
      </div>
    </FieldFrame>
  );
}
